// src/pages/Purchases.jsx
import React, { useMemo, useState, useRef } from "react";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  startAt,
  endAt,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { receiveFromPO, receiveManual } from "../services/inventoryApi";
import { recordInventoryMove } from "../services/stockMoves";

const DEFAULT_LOCATION_ID = "boutique";

function rid() {
  return (
    (crypto?.randomUUID && crypto.randomUUID()) ||
    Math.random().toString(36).slice(2)
  );
}

export default function Purchases() {
  // ---------- États de base ----------
  const [poId, setPoId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [locationId, setLocationId] = useState(DEFAULT_LOCATION_ID);

  // Lignes: { rowId, itemId, name, barcode, qtyReceived, cost, newPrice }
  const [rows, setRows] = useState([]);
  const [barcode, setBarcode] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ---------- Focus ligne active ----------
  const [activeRowId, setActiveRowId] = useState(null);
  const lastFocusedRowId = useRef(null);

  // ---------- Pricing / Scan / Recherche ----------
  const [updatePrice, setUpdatePrice] = useState(true);
  const [defaultMargin, setDefaultMargin] = useState(0.35);
  const [scanning, setScanning] = useState(false);

  // Recherche par nom (prefix sur nameLower)
  const [nameTerm, setNameTerm] = useState("");
  const [nameSugs, setNameSugs] = useState([]);

  // ---------- Totaux ----------
  const totalQty = useMemo(
    () => rows.reduce((s, r) => s + Number(r.qtyReceived || 0), 0),
    [rows]
  );
  const totalCost = useMemo(
    () =>
      rows.reduce(
        (s, r) => s + Number(r.qtyReceived || 0) * Number(r.cost || 0),
        0
      ),
    [rows]
  );

  // ---------- Helpers ----------
  function computePriceFromCost(cost) {
    const c = Number(cost || 0);
    if (!updatePrice) return 0;
    if (Number.isFinite(defaultMargin) && defaultMargin > 0) {
      return Math.round(c * (1 + Number(defaultMargin)));
    }
    if (c < 1000) return Math.round(c * 1.8);
    if (c < 5000) return Math.round(c * 1.6);
    if (c < 20000) return Math.round(c * 1.45);
    if (c < 50000) return Math.round(c * 1.35);
    return Math.round(c * 1.25);
  }

  function updateRow(rowId, patch) {
    setRows((prev) =>
      prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r))
    );
  }

  async function fillRowFromItemId(rowId, itemId) {
    if (!itemId) return;
    try {
      const ref = doc(db, "itemsEK", itemId);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setMessage("ID d'article introuvable.");
        return;
      }
      const it = snap.data();
      const cost = Number(it.cost || 0);
      const suggested = computePriceFromCost(cost);
      updateRow(rowId, {
        itemId,
        name: it.name || "Article",
        barcode: it.barcode || "",
        cost,
        newPrice: suggested || Number(it.price || 0),
      });
    } catch (e) {
      console.error(e);
      setMessage("Erreur lors du chargement de l'article.");
    }
  }

  // ---------- Scan code-barres (mobile/desktop) ----------
  async function startScan() {
    setMessage("");
    if (!("BarcodeDetector" in window)) {
      setMessage(
        "Scanner non supporté sur ce navigateur. Utilise la saisie manuelle."
      );
      return;
    }
    try {
      const detector = new window.BarcodeDetector({
        formats: ["ean_13", "code_128", "upc_a", "upc_e"],
      });
      setScanning(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();

      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = 480;
      const ctx = canvas.getContext("2d");

      const tryDetect = async () => {
        if (!scanning) return;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const bitmap = await createImageBitmap(canvas);
        const codes = await detector.detect(bitmap);
        if (codes.length) {
          const code = codes[0].rawValue;
          stream.getTracks().forEach((t) => t.stop());
          setScanning(false);
          await addByBarcode(code);
        } else {
          requestAnimationFrame(tryDetect);
        }
      };
      requestAnimationFrame(tryDetect);
    } catch (e) {
      console.error(e);
      setScanning(false);
      setMessage("Impossible d’ouvrir la caméra.");
    }
  }

  // ---------- Ajout par code-barres ----------
  async function addByBarcode(code) {
    setMessage("");
    setLoading(true);
    try {
      const qy = query(
        collection(db, "itemsEK"),
        where("barcode", "==", code),
        limit(1)
      );
      const snap = await getDocs(qy);
      if (snap.empty) {
        setMessage("Code inconnu : " + code);
        return;
      }
      const d = snap.docs[0];
      const it = d.data();

      const found = rows.find((r) => r.itemId === d.id);
      if (found) {
        updateRow(found.rowId, {
          qtyReceived: Number(found.qtyReceived || 0) + 1,
        });
        setActiveRowId(found.rowId);
      } else {
        const suggestedPrice = computePriceFromCost(Number(it.cost || 0));
        const newId = rid();
        setRows((prev) => [
          ...prev,
          {
            rowId: newId,
            itemId: d.id,
            name: it.name || "Article",
            barcode: it.barcode || code,
            qtyReceived: 1,
            cost: Number(it.cost || 0),
            newPrice: suggestedPrice || Number(it.price || 0),
          },
        ]);
        setActiveRowId(newId);
      }
    } catch (e) {
      console.error(e);
      setMessage("Erreur lecture itemsEK par code-barres.");
    } finally {
      setLoading(false);
      setBarcode("");
    }
  }

  // ---------- Recherche par nom ----------
  async function searchByName() {
    const t = nameTerm.trim().toLowerCase();
    if (!t) {
      setNameSugs([]);
      return;
    }
    const qy = query(
      collection(db, "itemsEK"),
      orderBy("nameLower"),
      startAt(t),
      endAt(t + "\uf8ff"),
      limit(10)
    );
    const snap = await getDocs(qy);
    const arr = [];
    snap.forEach((d) => {
      const it = d.data();
      arr.push({
        id: d.id,
        name: it.name || "Article",
        barcode: it.barcode || "",
        cost: Number(it.cost || 0),
        price: Number(it.price || 0),
      });
    });
    setNameSugs(arr);
  }

  function pickNameSuggestion(s) {
    const suggested = computePriceFromCost(s.cost);
    const newId = rid();
    setRows((prev) => [
      ...prev,
      {
        rowId: newId,
        itemId: s.id,
        name: s.name,
        barcode: s.barcode,
        qtyReceived: 1,
        cost: s.cost,
        newPrice: suggested || s.price,
      },
    ]);
    setActiveRowId(newId);
    setNameSugs([]);
    setNameTerm("");
  }

  // ---------- Submit ----------
  async function handleSubmit() {
    if (rows.length === 0) {
      setMessage("Ajoute au moins une ligne avant d’enregistrer.");
      return;
    }

    for (const r of rows) {
      if (!r.itemId) {
        setMessage("Une ligne n’a pas d’article (itemId manquant).");
        return;
      }
      if (!Number(r.qtyReceived || 0)) {
        setMessage("Les quantités doivent être > 0.");
        return;
      }
    }

    setLoading(true);
    try {
      const items = rows.map((r) => ({
        itemId: r.itemId,
        qty: Number(r.qtyReceived || 0),
        unitCost: Number(r.cost || 0),
        newPrice: updatePrice ? Number(r.newPrice || 0) : undefined,
        margin: updatePrice ? Number(defaultMargin || 0) : undefined,
      }));

      const isPO = !!poId.trim();
      const poRef = poId.trim() || null;

      if (isPO) {
        await receiveFromPO({
          poId: poId.trim(),
          items,
          locationId,
          receivedBy: "anonymous",
        });
      } else {
        await receiveManual({
          items,
          locationId,
          receivedBy: "anonymous",
        });
      }

      const noteBase = isPO
        ? `Réception PO ${poId.trim()}`
        : "Réception manuelle";
      const fullNote = supplierName.trim()
        ? `${noteBase} - ${supplierName.trim()}`
        : noteBase;

      await Promise.all(
        rows.map((r) =>
          recordInventoryMove({
            itemId: r.itemId,
            locationId,
            type: "reception",
            delta: Number(r.qtyReceived || 0),
            note: fullNote,
            ref: poRef,
          })
        )
      );

      setMessage("✅ Réception enregistrée.");
      setRows([]);
      setPoId("");
      setSupplierName("");
      setActiveRowId(null);
      lastFocusedRowId.current = null;
    } catch (e) {
      console.error(e);
      setMessage("Erreur: " + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ---------- Rendu ----------
  return (
    <div className="container py-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="mb-0">📥 Réception d’achats</h3>
          <small className="text-muted">
            Scanne un code-barres ou recherche un nom, saisis les quantités
            reçues et valide.
          </small>
        </div>
        <button
          className="btn btn-outline-secondary"
          type="button"
          onClick={() => {
            const newId = rid();
            setRows((prev) => [
              ...prev,
              {
                rowId: newId,
                itemId: "",
                name: "",
                barcode: "",
                qtyReceived: 1,
                cost: 0,
                newPrice: 0,
              },
            ]);
            setActiveRowId(newId);
          }}
        >
          + Ligne
        </button>
      </div>

      {/* En-tête */}
      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-12 col-md-3">
            <label className="form-label">PO ID (optionnel)</label>
            <input
              className="form-control"
              placeholder="ex: abc123"
              value={poId}
              onChange={(e) => setPoId(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Fournisseur</label>
            <input
              className="form-control"
              placeholder="Nom du fournisseur"
              value={supplierName}
              onChange={(e) => setSupplierName(e.target.value)}
            />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label">Emplacement</label>
            <input
              className="form-control"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="ex: boutique"
            />
          </div>

          {/* Scan bloc */}
          <div className="col-12 col-md-6">
            <label className="form-label">Scanner (mobile/desktop)</label>
            <div className="d-flex gap-2">
              <input
                className="form-control"
                placeholder="EAN / UPC"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  barcode.trim() &&
                  addByBarcode(barcode.trim())
                }
              />
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={startScan}
                disabled={scanning}
              >
                {scanning ? "Scan..." : "Scanner"}
              </button>
            </div>
            <div className="form-text">
              Si l’appareil le supporte, la caméra s’ouvrira pour lire le
              code-barres.
            </div>
          </div>

          {/* Recherche par nom */}
          <div className="col-12 col-md-6">
            <label className="form-label">Rechercher par nom</label>
            <div className="d-flex gap-2 position-relative">
              <input
                className="form-control"
                placeholder="Saisir un nom..."
                value={nameTerm}
                onChange={(e) => setNameTerm(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && searchByName()}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={searchByName}
              >
                Chercher
              </button>
              {nameSugs.length > 0 && (
                <div
                  className="list-group position-absolute w-100 mt-5 shadow"
                  style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                >
                  {nameSugs.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => pickNameSuggestion(s)}
                    >
                      <div className="fw-semibold">{s.name}</div>
                      <small className="text-muted">
                        CB: {s.barcode || "—"} · Coût:{" "}
                        {s.cost.toLocaleString()} · Prix:{" "}
                        {s.price.toLocaleString()}
                      </small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Paramètres de pricing */}
          <div className="col-12">
            <label className="form-label">Mise à jour du prix de vente</label>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="form-check">
                <input
                  id="updPrice"
                  type="checkbox"
                  className="form-check-input"
                  checked={updatePrice}
                  onChange={(e) => setUpdatePrice(e.target.checked)}
                />
                <label htmlFor="updPrice" className="form-check-label">
                  Mettre à jour le prix de vente
                </label>
              </div>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className="form-control"
                  style={{ maxWidth: 140 }}
                  value={defaultMargin}
                  onChange={(e) =>
                    setDefaultMargin(Number(e.target.value || 0))
                  }
                  title="Marge par défaut (ex: 0.35 = +35%)"
                />
                <span className="text-muted">marge</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tableau / cartes */}
      <div className="card">
        <div className="card-body">
          {/* Desktop table */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table align-middle">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Nom</th>
                    <th style={{ width: 120 }}>Qté reçue</th>
                    <th style={{ width: 140 }}>Coût (XOF)</th>
                    <th style={{ width: 140 }}>Prix vente (XOF)</th>
                    <th style={{ width: 140 }}>Total (XOF)</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-muted">
                        Aucune ligne. Scanne un code-barres ou cherche par nom.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => {
                      const lineTotal =
                        Number(r.qtyReceived || 0) * Number(r.cost || 0);
                      return (
                        <tr key={r.rowId}>
                          <td>
                            <input
                              className="form-control"
                              placeholder="Nom affiché (optionnel)"
                              value={r.name}
                              onChange={(e) =>
                                updateRow(r.rowId, { name: e.target.value })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              value={r.qtyReceived}
                              onChange={(e) =>
                                updateRow(r.rowId, {
                                  qtyReceived: Number(e.target.value || 0),
                                })
                              }
                              ref={(el) => {
                                if (
                                  typeof window !== "undefined" &&
                                  window.innerWidth >= 768 && // desktop seulement
                                  el &&
                                  r.rowId === activeRowId &&
                                  lastFocusedRowId.current !== activeRowId
                                ) {
                                  lastFocusedRowId.current = activeRowId;
                                  el.focus();
                                  el.select?.();
                                  el.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                }
                              }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              value={r.cost}
                              onChange={(e) => {
                                const cost = Number(e.target.value || 0);
                                const auto = computePriceFromCost(cost);
                                updateRow(r.rowId, {
                                  cost,
                                  newPrice: updatePrice ? auto : r.newPrice,
                                });
                              }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              className="form-control"
                              value={r.newPrice ?? 0}
                              disabled={!updatePrice}
                              onChange={(e) =>
                                updateRow(r.rowId, {
                                  newPrice: Number(e.target.value || 0),
                                })
                              }
                            />
                          </td>
                          <td className="fw-semibold">
                            {lineTotal.toLocaleString()}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() =>
                                setRows((prev) =>
                                  prev.filter((x) => x.rowId !== r.rowId)
                                )
                              }
                            >
                              Retirer
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2} className="text-end fw-bold">
                      Totaux
                    </td>
                    <td className="fw-bold">
                      {totalQty.toLocaleString()}
                    </td>
                    <td></td>
                    <td className="fw-bold">
                      {totalCost.toLocaleString()} XOF
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Mobile layout */}
          <div className="d-block d-md-none">
            {rows.length === 0 ? (
              <div className="text-muted">
                Aucune ligne. Scanne un code-barres ou cherche par nom.
              </div>
            ) : (
              rows.map((r) => {
                const lineTotal =
                  Number(r.qtyReceived || 0) * Number(r.cost || 0);
                return (
                  <div key={r.rowId} className="card mb-2">
                    <div className="card-body">
                      <div className="mb-2">
                        <label className="form-label small mb-1">Nom</label>
                        <input
                          className="form-control"
                          placeholder="Nom affiché (optionnel)"
                          value={r.name}
                          onChange={(e) =>
                            updateRow(r.rowId, { name: e.target.value })
                          }
                        />
                      </div>

                      <div className="row g-2">
                        <div className="col-6">
                          <label className="form-label small mb-1">
                            Qté reçue
                          </label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={r.qtyReceived}
                            onChange={(e) =>
                              updateRow(r.rowId, {
                                qtyReceived: Number(e.target.value || 0),
                              })
                            }
                            ref={(el) => {
                              if (
                                typeof window !== "undefined" &&
                                window.innerWidth < 768 && // mobile seulement
                                el &&
                                r.rowId === activeRowId &&
                                lastFocusedRowId.current !== activeRowId
                              ) {
                                lastFocusedRowId.current = activeRowId;
                                el.focus();
                                el.select?.();
                                el.scrollIntoView({
                                  behavior: "smooth",
                                  block: "center",
                                });
                              }
                            }}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label small mb-1">
                            Coût (XOF)
                          </label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={r.cost}
                            onChange={(e) => {
                              const cost = Number(e.target.value || 0);
                              const auto = computePriceFromCost(cost);
                              updateRow(r.rowId, {
                                cost,
                                newPrice: updatePrice ? auto : r.newPrice,
                              });
                            }}
                          />
                        </div>
                        <div className="col-6">
                          <label className="form-label small mb-1">
                            Prix vente (XOF)
                          </label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={r.newPrice ?? 0}
                            disabled={!updatePrice}
                            onChange={(e) =>
                              updateRow(r.rowId, {
                                newPrice: Number(e.target.value || 0),
                              })
                            }
                          />
                        </div>
                        <div className="col-6 d-flex flex-column justify-content-end">
                          <div className="small text-muted">Total</div>
                          <div className="fw-semibold">
                            {lineTotal.toLocaleString()} XOF
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-2">
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() =>
                            setRows((prev) =>
                              prev.filter((x) => x.rowId !== r.rowId)
                            )
                          }
                        >
                          Retirer
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            {/* Totaux mobile */}
            <div className="border-top pt-2 mt-2">
              <div className="d-flex justify-content-between">
                <span className="small text-muted">Total quantité</span>
                <span className="fw-bold">
                  {totalQty.toLocaleString()}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="small text-muted">Total coût</span>
                <span className="fw-bold">
                  {totalCost.toLocaleString()} XOF
                </span>
              </div>
            </div>
          </div>

          {/* Actions bas de carte */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="small text-muted">{message || "—"}</div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => {
                  setRows([]);
                  setMessage("");
                  setActiveRowId(null);
                  lastFocusedRowId.current = null;
                }}
                disabled={loading}
              >
                Vider
              </button>
              <button
                className="btn btn-dark"
                type="button"
                onClick={handleSubmit}
                disabled={loading || rows.length === 0}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" />
                ) : null}
                Enregistrer la réception
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-3 text-muted small">
        Astuce : pour la recherche par nom performante, assure-toi de stocker{" "}
        <code>nameLower</code> dans
        <code> itemsEK</code> (<code>nameLower = name.toLowerCase()</code>).
      </div>
    </div>
  );
}
